import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Collapse, Typography,
} from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ReactMarkdown from 'react-markdown';
import { sanitizeHtml } from '../../../utils/safeHtml';
import { useDashboard } from '../../../contexts/DashboardContext';
import PanelExportActions from '../PanelExportActions';

export default function DashboardRichText({ panelId, outputKey = 'rich_text', config }) {
  const { getSlice, exportPanelArtifact, downloadProjectSummary, config: dashConfig } = useDashboard();
  const isCitizen = Boolean(
    dashConfig?.citizenMode || dashConfig?.metadata?.interviewFlow?.citizenMode,
  );
  const [summaryBusy, setSummaryBusy] = useState(false);
  const raw = getSlice(panelId, outputKey);

  const content = useMemo(() => {
    if (!raw) return null;
    if (typeof raw === 'string') return raw;
    if (typeof raw === 'object' && raw.content) return raw.content;
    if (typeof raw === 'object' && raw.html) return raw.html;
    return String(raw);
  }, [raw]);

  const looksLikeRecommendedPath = /##\s*Recommended path/i.test(String(content || ''));
  // Citizen Applications gets noisy when the full path summary lands here — start collapsed.
  const [detailsOpen, setDetailsOpen] = useState(() => {
    if (config?.collapsedDefault === true) return false;
    if (isCitizen && looksLikeRecommendedPath) return false;
    return true;
  });

  useEffect(() => {
    if (isCitizen && looksLikeRecommendedPath) setDetailsOpen(false);
  }, [isCitizen, looksLikeRecommendedPath, content]);

  if (!content) {
    const emptyHint = config?.emptyMessage
      || 'Enter or pin a San Francisco address — we validate the parcel and list constraints here.';
    return (
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 200 }}>
        <Typography color="text.secondary" sx={{ textAlign: 'center', maxWidth: 360 }}>
          {emptyHint}
        </Typography>
      </Box>
    );
  }

  const isHtml = typeof raw === 'object' && raw.html;
  const safeHtml = isHtml ? sanitizeHtml(content) : '';
  /** InterviewFlow + any Studio V2 / App Builder runtime that provides exportPanelArtifact */
  const hasExport = typeof exportPanelArtifact === 'function';
  const hasProjectSummary = typeof downloadProjectSummary === 'function';
  const looksLikeSfSummary = /Recommended path|Land-use clearance|Fee estimates|Address validation/i.test(String(content));
  const collapseLongPath = isCitizen && looksLikeRecommendedPath;

  const onDownloadProject = async () => {
    setSummaryBusy(true);
    try {
      await downloadProjectSummary();
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert(err?.message || 'Could not download project summary');
    } finally {
      setSummaryBusy(false);
    }
  };

  const body = isHtml ? (
    <Box
      data-testid="rich-text-html"
      dangerouslySetInnerHTML={{ __html: safeHtml }}
      sx={{
        '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 1 },
        '& p': { mb: 1 },
        '& ul, & ol': { pl: 3, mb: 1 },
      }}
    />
  ) : (
    <ReactMarkdown
      components={{
        h1: ({ children }) => <Typography variant="h4" sx={{ mt: 2, mb: 1 }}>{children}</Typography>,
        h2: ({ children }) => <Typography variant="h5" sx={{ mt: 2, mb: 1 }}>{children}</Typography>,
        h3: ({ children }) => <Typography variant="h6" sx={{ mt: 1, mb: 1 }}>{children}</Typography>,
        p: ({ children }) => <Typography variant="body1" sx={{ mb: 1 }}>{children}</Typography>,
        ul: ({ children }) => <Box component="ul" sx={{ pl: 3, mb: 1 }}>{children}</Box>,
        ol: ({ children }) => <Box component="ol" sx={{ pl: 3, mb: 1 }}>{children}</Box>,
        li: ({ children }) => <Box component="li" sx={{ mb: 0.5 }}>{children}</Box>,
      }}
    >
      {content}
    </ReactMarkdown>
  );

  return (
    <Box sx={{
      p: 2,
      height: isCitizen ? 'auto' : '100%',
      overflow: isCitizen ? 'visible' : 'auto',
    }}
    >
      {(hasExport || (hasProjectSummary && looksLikeSfSummary)) && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 1 }}>
          {hasExport && (
            <PanelExportActions
              panelId={panelId}
              panelLabel={config?.title || 'Summary'}
              disabled={!content}
              onExportJson={() => exportPanelArtifact?.(panelId, 'json')}
              onExportPdf={() => exportPanelArtifact?.(panelId, 'pdf')}
            />
          )}
          {hasProjectSummary && looksLikeSfSummary && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              disabled={summaryBusy || !content}
              onClick={onDownloadProject}
              data-testid="download-project-summary"
            >
              {summaryBusy ? 'Preparing…' : 'Project summary'}
            </Button>
          )}
        </Box>
      )}
      {collapseLongPath ? (
        <Box data-testid="rich-text-recommended-path-collapse">
          <Button
            size="small"
            onClick={() => setDetailsOpen((v) => !v)}
            endIcon={detailsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ textTransform: 'none', color: 'text.secondary', px: 0.5, mb: 0.5 }}
          >
            {detailsOpen ? 'Hide recommended path details' : 'Show recommended path details'}
          </Button>
          <Collapse in={detailsOpen}>{body}</Collapse>
        </Box>
      ) : body}
    </Box>
  );
}
